<template>
    <div>
        <component :is="layout">
            <router-view/>
        </component>
        <snack-bar/>
    </div>
</template>

<script setup>
import {computed, watch} from 'vue'
import {useRoute} from 'vue-router'
import Auth from '@/layouts/Auth.vue'
import Guest from '@/layouts/Guest.vue'
import Wizard from '@/layouts/Wizard.vue'
import Driver from '@/layouts/Driver.vue'
import {useNavigation} from "@/composables/globals";
import {useAuthStore} from "@/stores/authStore";

const $route = useRoute()
const authStore = useAuthStore();
const {navigateTo} = useNavigation();

const needsSignIn = computed(
    () => !authStore.isAuthenticated() && $route.meta.layout === 'auth'
);

// Not from inside the computed below. Clearing the session re-runs that
// computed before the push to login has settled its async guard, so it fired a
// second navigation and, because navigateTo returns nothing, rendered an
// undefined component - a blank frame on the way out of every sign out.
watch(needsSignIn, (needed) => {
    if (needed) {
        navigateTo('login');
    }
}, {immediate: true});

const layout = computed(() => {
    // The retired driver web app notice under /drive: no manager session, so
    // a signed-in manager is not bounced to the dashboard and a driver is not
    // shown the manager login.
    if ($route.meta.layout === 'driver') {
        return Driver;
    }
    if (needsSignIn.value) {
        // Guest, not nothing, while the redirect above is in flight.
        return Guest;
    } else if ($route.meta.layout === 'wizard' && authStore.isAuthenticated()) {
        return Wizard;
    } else if (authStore.isAuthenticated() && $route.meta.layout !== 'guest') {
        return Auth;
    } else {
        return Guest;
    }
})
</script>

<style>
#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
}

nav {
    padding: 30px;
}

nav a {
    font-weight: bold;
    color: #2c3e50;
}

nav a.router-link-exact-active {
    color: #42b983;
}

/* Driver.js Product Tour Customization */
.driver-popover.simpligps-tour-popover {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    max-width: 380px;
}

.driver-popover.simpligps-tour-popover .driver-popover-title {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a2e;
}

.driver-popover.simpligps-tour-popover .driver-popover-description {
    font-size: 14px;
    color: #555;
    line-height: 1.5;
}

.driver-popover.simpligps-tour-popover .driver-popover-progress-text {
    font-size: 12px;
    color: #888;
}

.driver-popover.simpligps-tour-popover button.driver-popover-next-btn {
    background-color: #551A8B;
    color: #fff;
    border-radius: 6px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    text-shadow: none;
}

.driver-popover.simpligps-tour-popover button.driver-popover-next-btn:hover {
    background-color: #6B21A8;
}

.driver-popover.simpligps-tour-popover button.driver-popover-prev-btn {
    border-radius: 6px;
    padding: 6px 16px;
    font-size: 13px;
    color: #551A8B;
    border: 1px solid #551A8B;
    text-shadow: none;
}

.driver-popover.simpligps-tour-popover button.driver-popover-close-btn {
    color: #888;
}

.driver-popover.simpligps-tour-popover .driver-popover-footer {
    gap: 8px;
}
</style>
